import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useDebounceValue } from 'components/hooks/useDebouncedValue';
import { TFunction } from 'i18next';

export type Search = {
  searchString: string;
  setSearchString: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  isBelowMinimum: boolean;
  isDebouncing: boolean;
  resetSearch: () => void;
};

export const MINIMUM_SEARCH_CHARACTER_LIMIT = 2;
export const MINIMUM_SEARCH_CHARACTER_LIMIT_STARTS_WITH = 2;
export const MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS = 3;

/**
 * Optional value to be used in api calls to prevent unnecessary refetching in scenarios where data rarely changes
 */
export const STALE_TIME_MS = 30_000;
export const useSearch = (
  minCharacters: number = MINIMUM_SEARCH_CHARACTER_LIMIT,
): Search => {
  const [searchString, setSearchString] = useState('');
  const debounced = useDebounceValue(searchString);
  const searchTerm = useMemo(
    () => (debounced.length >= minCharacters ? debounced : ''),
    [debounced, minCharacters],
  );
  const trimmedSearchString = searchString.trim();

  const isBelowMinimum =
    trimmedSearchString.length > 0 &&
    trimmedSearchString.length < minCharacters;

  const isDebouncing = trimmedSearchString !== debounced.trim();
  const resetSearch = () => setSearchString('');

  return {
    searchString,
    setSearchString,
    searchTerm,
    isBelowMinimum,
    isDebouncing,
    resetSearch,
  };
};

export const STARTS_WITH = 'STARTS_WITH' as const;
export const CONTAINS = 'CONTAINS' as const;
export const SEARCH_MODES = [STARTS_WITH, CONTAINS];
export type SearchMode = (typeof SEARCH_MODES)[number];

export const getMinimumSearchCharacters = (searchMode: SearchMode): number =>
  searchMode === STARTS_WITH
    ? MINIMUM_SEARCH_CHARACTER_LIMIT_STARTS_WITH
    : MINIMUM_SEARCH_CHARACTER_LIMIT_CONTAINS;

export const getStartsWithContainsFilterOptions = (
  t: TFunction,
): { value: SearchMode; label: string }[] => [
  {
    value: STARTS_WITH,
    label: t('ui.event-management.list-filter.mode.start-with'),
  },
  {
    value: CONTAINS,
    label: t('ui.event-management.list-filter.mode.contains'),
  },
];

declare const MinLengthSymbol: unique symbol;

export type MinLengthString<Min extends number> = string & {
  readonly [MinLengthSymbol]: Min;
};

export function isMinLengthString<N extends number>(
  str: string,
  min: N,
): str is MinLengthString<N> {
  return str.length >= min;
}

export type DefaultPageParam = {
  query?: string;
  responseSize?: number;
  searchTerm?: string;
};
export type QueryParamsBase = {
  [key: string]: QueryParamValue;
};
export type QueryParamValue =
  string | number | boolean | null | undefined | QueryParamsBase;
